import { useState, useEffect } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User, Mail, Lock, Info, Building2, Phone, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog';
import { supabase } from '@/integrations/supabase/client';
import { isRecoveryLocked } from '@/lib/recoveryLock';

// Helper to translate common Supabase auth errors to PT-BR
function translateAuthError(message: string): string {
  if (message.includes('Invalid login credentials')) return 'E-mail ou senha incorretos.';
  if (message.toLowerCase().includes('banned')) return 'Perfil suspenso ou inativado. Contate o administrador.'; // §I.146
  if (message.includes('Email not confirmed')) return 'E-mail não confirmado. Verifique sua caixa de entrada.';
  if (message.includes('User already registered')) return 'Este e-mail já está cadastrado.';
  if (message.includes('Password should be')) return 'A senha deve ter pelo menos 6 caracteres.';
  if (message.includes('rate limit')) return 'Muitas tentativas. Aguarde um momento.';
  return message;
}

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signIn, signUp, loading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'signin');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const isNewUser = searchParams.get('new') === 'true';
  const passwordChanged = searchParams.get('senha') === 'alterada'; // AUTH-CADASTRO Ato 1
  const [hasCompletedInitialCleanup, setHasCompletedInitialCleanup] = useState(!isNewUser);
  // I.173 CADASTRO-FECHADO: a aba "Cadastrar" segue a chave da plataforma (só o master do sistema muda);
  // enquanto lê, ou se a leitura falhar, vale fechado
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    supabase.rpc('is_public_signup_open').then(({ data, error }) => {
      if (active) setSignupOpen(!error && data === true);
    });
    return () => { active = false; };
  }, []);

  // Force logout for new users coming from email invitation
  useEffect(() => {
    const handleNewUserFlow = async () => {
      if (isNewUser) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('🔄 Novo usuário - limpando sessão existente para permitir login');
          await supabase.auth.signOut();
        }
        setHasCompletedInitialCleanup(true);
        return;
      }

      // Clean up inconsistent sessions
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        if (error || !profile) {
          console.log('🧹 Limpando sessão sem perfil válido');
          await supabase.auth.signOut();
        }
      }
    };
    
    handleNewUserFlow();
  }, [isNewUser]);

  // ✅ FIXED: Redirect already-authenticated users with role check
  // Only redirect for existing sessions (not fresh logins — those use explicit navigate)
  // AUTH-CADASTRO Ato 1: com a trava de recuperação, quem decide o destino é o RecoveryLockGate
  if (user && !loading && hasCompletedInitialCleanup && !isSubmitting && !isRecoveryLocked()) {
    const isDriverMode = localStorage.getItem('gonx-driver-mode') === 'true';
    if (isDriverMode) {
      return <Navigate to="/driver" replace />;
    }
    localStorage.removeItem('gonx-driver-mode');
    return <Navigate to="/dashboard" replace />;
  }

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const { error } = await signIn(email, password);
    
    if (error) {
      setFormError(translateAuthError(error.message || 'Ocorreu um erro inesperado'));
      setIsSubmitting(false);
      return;
    }

    // ✅ FIXED: signIn now awaits profile loading — navigate explicitly
    localStorage.removeItem('gonx-driver-mode');
    navigate('/dashboard', { replace: true });
    setIsSubmitting(false);
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (signupOpen !== true) {
      setFormError('O cadastro de novas empresas está fechado no momento.');
      return;
    }
    setIsSubmitting(true);
    setFormError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const fullName = formData.get('fullName') as string;
    const companyName = formData.get('companyName') as string;
    const phone = formData.get('phone') as string;

    const { error } = await signUp(email, password, fullName, companyName, phone || undefined);
    
    if (error) {
      setFormError(translateAuthError(error.message || 'Erro ao cadastrar'));
    } else {
      setActiveTab('signin');
    }
    
    setIsSubmitting(false);
  };

  if (loading && !isSubmitting) {
    return (
      <div className="min-h-screen bg-gradient-industrial flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-primary rounded-full animate-pulse"></div>
          <div className="w-4 h-4 bg-primary rounded-full animate-pulse animation-delay-150"></div>
          <div className="w-4 h-4 bg-primary rounded-full animate-pulse animation-delay-300"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-industrial flex items-center justify-center p-4 relative overflow-y-auto">
      <Button
        variant="ghost"
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Voltar para o site
      </Button>
      <div className="w-full max-w-md">
        {/* Header — responsive logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="/gonx-logo.png" 
              alt="GOnX Logo" 
              className="h-40 md:h-72 w-auto"
            />
          </div>
        </div>

        {/* Auth Card */}
        <Card className="shadow-lg border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Acesso ao Sistema</CardTitle>
            <CardDescription>
              {signupOpen ? 'Entre com suas credenciais ou cadastre-se' : 'Entre com suas credenciais'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isNewUser && (
              <Alert className="mb-4 border-primary/50 bg-primary/5">
                <Info className="h-4 w-4 text-primary" />
                <AlertDescription className="text-sm">
                  <strong>Bem-vindo ao GONX!</strong><br />
                  Use o <strong>email</strong> e a <strong>senha temporária</strong> que você recebeu para fazer seu primeiro acesso.
                </AlertDescription>
              </Alert>
            )}

            {/* ✅ Inline error alert */}
            {formError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
            
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setFormError(null); }} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Entrar</TabsTrigger>
                <TabsTrigger value="signup">Cadastrar</TabsTrigger>
              </TabsList>

              {/* Sign In Tab */}
              <TabsContent value="signin" className="mt-6">
                {passwordChanged && (
                  <Alert className="mb-4">
                    <Info className="h-4 w-4" />
                    <AlertDescription>Senha alterada com sucesso. Entre com a nova senha.</AlertDescription>
                  </Alert>
                )}
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signin-email"
                        name="email"
                        type="email"
                        placeholder="seu@email.com"
                        className="pl-10"
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Senha</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signin-password"
                        name="password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        required
                        autoComplete="current-password"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="link"
                        className="text-sm p-0 h-auto"
                        onClick={() => setShowForgotPassword(true)}
                      >
                        Esqueci minha senha
                      </Button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Entrando...' : 'Entrar'}
                  </Button>
                </form>
              </TabsContent>

              {/* Sign Up Tab */}
              <TabsContent value="signup" className="mt-6">
                {signupOpen === null ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Carregando...</p>
                ) : signupOpen === false ? (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      O cadastro de novas empresas está fechado no momento.<br />
                      Já tem acesso? Use a aba <strong>Entrar</strong>.
                    </AlertDescription>
                  </Alert>
                ) : (
                <>
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Nome Completo</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-name"
                        name="fullName"
                        type="text"
                        placeholder="Seu nome completo"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-company">Nome da Empresa</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-company"
                        name="companyName"
                        type="text"
                        placeholder="Nome da sua empresa"
                        className="pl-10"
                        required
                        minLength={2}
                        maxLength={100}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-phone">Telefone / WhatsApp <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-phone"
                        name="phone"
                        type="tel"
                        placeholder="(00) 00000-0000"
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-email"
                        name="email"
                        type="email"
                        placeholder="seu@email.com"
                        className="pl-10"
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Senha</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="signup-password"
                        name="password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        required
                        autoComplete="new-password"
                        minLength={6}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Mínimo de 6 caracteres
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Cadastrando...' : 'Cadastrar'}
                  </Button>
                </form>

                <div className="mt-4">
                  <Separator />
                  <p className="text-xs text-muted-foreground text-center mt-4">
                    Ao se cadastrar, sua empresa será criada automaticamente e você será o <strong>administrador</strong>.
                  </p>
                </div>
                </>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-muted-foreground">
          <p>© 2024 GOnX - Softwares de Gestão</p>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={showForgotPassword} 
        onOpenChange={setShowForgotPassword}
      />
    </div>
  );
}